/**
 * Deterministic itinerary checks ("Needs attention").
 * Each finding carries severity, involved events, evidence, an optional calculation, the time
 * it was checked, and a recommended next step. Heuristics are labelled as such; nothing here is
 * an operator guarantee. Fingerprints are stable for unchanged inputs so dismiss/snooze/resolve
 * actions persist, and change when the inputs change so a fixed issue disappears and a new one
 * is not hidden by an old dismissal.
 */
import { concernsUser } from './now';
import { TRANSPORT_CATEGORIES, normLocal, type EventWithPeople, type Trip, type TripStop } from './model';
import { addDays, formatDuration, minutesBetween, utcToLocal } from './time';

export type Severity = 'high' | 'medium' | 'low';
export type FindingKind =
  | 'overlap' | 'zone_missing' | 'zone_ambiguous' | 'zone_nonexistent' | 'time_missing'
  | 'short_connection' | 'station_change' | 'hotel_date_mismatch' | 'no_accommodation'
  | 'outside_trip_dates';

export interface Finding {
  fingerprint: string;
  kind: FindingKind;
  severity: Severity;
  title: string;
  impact: string;
  evidence: string[];
  calculation?: string;
  eventIds: string[];
  /** People the finding concerns (empty = everyone on the trip who can see the events). */
  personIds: string[];
  action: { label: string; kind: 'edit_event' | 'add_event' | 'review' };
  checkedAt: string;
  /** True when the check could not be completed with the data available. */
  incomplete: boolean;
}

export interface CheckOptions {
  now: Date;
  /** Minimum connection between consecutive transport legs, minutes (user-configurable buffer). */
  minConnectionMinutes?: number;
  /** Everyone on the trip, used to evaluate per-person overlaps. */
  memberIds: string[];
}

const ACTIVE = (e: EventWithPeople) => e.status !== 'canceled' && e.status !== 'suggested';

function hash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

function fp(kind: FindingKind, events: EventWithPeople[], extra = ''): string {
  const ids = events.map((e) => e.id).sort();
  const inputs = events.map((e) => `${e.id}:${e.start_utc}:${e.end_utc}:${e.start_date}:${e.end_date}:${e.start_tz}:${e.status}`).sort().join('|');
  return `${kind}:${ids.join(',')}:${hash(inputs + extra)}`;
}

function who(e: EventWithPeople, memberIds: string[]): string[] {
  return memberIds.filter((m) => concernsUser(e, m));
}

function label(e: EventWithPeople): string {
  return `“${e.title}”`;
}

function localLabel(e: EventWithPeople, which: 'start' | 'end'): string {
  const utc = which === 'start' ? e.start_utc : e.end_utc;
  const tz = which === 'start' ? e.start_tz : e.end_tz ?? e.start_tz;
  if (utc && tz) return `${utcToLocal(utc, tz).replace('T', ' ')} (${tz})`;
  const local = normLocal(which === 'start' ? e.start_local : e.end_local);
  return local ? `${local.replace('T', ' ')} (zone unknown)` : 'time not set';
}

export function checkItinerary(trip: Trip, stops: TripStop[], events: EventWithPeople[], opts: CheckOptions): Finding[] {
  const checkedAt = opts.now.toISOString();
  const minConn = opts.minConnectionMinutes ?? 45;
  const out: Finding[] = [];
  const live = events.filter(ACTIVE);

  // 1. Time and zone problems on individual events
  for (const e of live) {
    for (const which of ['start', 'end'] as const) {
      const precision = which === 'start' ? e.start_precision : e.end_precision;
      const status = which === 'start' ? e.start_zone_status : e.end_zone_status;
      if (precision !== 'datetime') continue;
      if (status === 'no_zone') {
        out.push({
          fingerprint: fp('zone_missing', [e], which), kind: 'zone_missing', severity: TRANSPORT_CATEGORIES.has(e.category) ? 'high' : 'medium',
          title: `Time zone missing for ${label(e)}`,
          impact: 'Without a time zone this time cannot be placed on the timeline, and connection checks are incomplete.',
          evidence: [`${which === 'start' ? 'Start' : 'End'}: ${localLabel(e, which)}`, ...(e.start_raw ? [`As written: ${e.start_raw}`] : [])],
          eventIds: [e.id], personIds: who(e, opts.memberIds), action: { label: 'Set time zone', kind: 'edit_event' }, checkedAt, incomplete: true,
        });
      } else if (status === 'ambiguous' || status === 'nonexistent') {
        const amb = status === 'ambiguous';
        out.push({
          fingerprint: fp(amb ? 'zone_ambiguous' : 'zone_nonexistent', [e], which), kind: amb ? 'zone_ambiguous' : 'zone_nonexistent', severity: 'high',
          title: amb ? `${label(e)} falls in a repeated hour` : `${label(e)} falls in a skipped hour`,
          impact: amb
            ? 'Clocks go back at this time, so this wall-clock time happens twice. Choose which one the booking means.'
            : 'Clocks go forward at this time, so this wall-clock time does not exist. Check the booking.',
          evidence: [`${which === 'start' ? 'Start' : 'End'}: ${localLabel(e, which)}`],
          eventIds: [e.id], personIds: who(e, opts.memberIds), action: { label: 'Confirm time', kind: 'edit_event' }, checkedAt, incomplete: true,
        });
      }
    }
    if (e.source === 'booking' && TRANSPORT_CATEGORIES.has(e.category) && e.start_precision !== 'datetime') {
      out.push({
        fingerprint: fp('time_missing', [e]), kind: 'time_missing', severity: 'medium',
        title: `Departure time missing for ${label(e)}`,
        impact: 'This booked journey has no departure time, so Now cannot tell you when to leave.',
        evidence: [e.start_date ? `Date only: ${e.start_date}` : 'No date or time recorded'],
        eventIds: [e.id], personIds: who(e, opts.memberIds), action: { label: 'Add departure time', kind: 'edit_event' }, checkedAt, incomplete: true,
      });
    }
  }

  // 2. Per-person overlaps between timed commitments (stays and free time excluded)
  const timed = live
    .filter((e) => e.start_utc && e.end_utc && e.category !== 'accommodation' && e.category !== 'free_time')
    .sort((a, b) => a.start_utc!.localeCompare(b.start_utc!));
  for (let i = 0; i < timed.length; i++) {
    for (let j = i + 1; j < timed.length; j++) {
      const a = timed[i]!; const b = timed[j]!;
      if (b.start_utc! >= a.end_utc!) break;
      const shared = who(a, opts.memberIds).filter((p) => who(b, opts.memberIds).includes(p));
      if (shared.length === 0) continue; // two subgroup outings at once are fine
      const overlap = minutesBetween(b.start_utc!, a.end_utc! < b.end_utc! ? a.end_utc! : b.end_utc!);
      out.push({
        fingerprint: fp('overlap', [a, b]), kind: 'overlap', severity: a.is_fixed && b.is_fixed ? 'high' : 'medium',
        title: `${label(a)} overlaps ${label(b)}`,
        impact: `The same ${shared.length === 1 ? 'person is' : `${shared.length} people are`} booked into both.`,
        evidence: [`${label(a)} ends ${localLabel(a, 'end')}`, `${label(b)} starts ${localLabel(b, 'start')}`],
        calculation: `Overlap: ${formatDuration(overlap)}`,
        eventIds: [a.id, b.id], personIds: shared, action: { label: 'Review times', kind: 'review' }, checkedAt, incomplete: false,
      });
    }
  }

  // 3. Connections between consecutive transport legs (per person)
  const legs = timed.filter((e) => TRANSPORT_CATEGORIES.has(e.category));
  for (const person of opts.memberIds) {
    const mine = legs.filter((e) => concernsUser(e, person));
    for (let i = 0; i + 1 < mine.length; i++) {
      const a = mine[i]!; const b = mine[i + 1]!;
      const gap = minutesBetween(a.end_utc!, b.start_utc!);
      if (gap < 0 || gap > 6 * 60) continue;
      const differentPlace = a.destination_name && b.origin_name && a.destination_name.trim().toLowerCase() !== b.origin_name.trim().toLowerCase();
      if (gap < minConn || differentPlace) {
        const kind: FindingKind = differentPlace ? 'station_change' : 'short_connection';
        const key = fp(kind, [a, b]);
        if (out.some((f) => f.fingerprint === key)) continue;
        out.push({
          fingerprint: key, kind, severity: gap < minConn ? 'high' : 'medium',
          title: differentPlace ? `Change of station between ${label(a)} and ${label(b)}` : `Short connection before ${label(b)}`,
          impact: differentPlace
            ? `You arrive at ${a.destination_name} but depart from ${b.origin_name}. Allow time to get between them.`
            : `Only ${formatDuration(gap)} between arrival and departure. Check the operator's minimum connection and boarding cutoff.`,
          evidence: [`Arrive ${localLabel(a, 'end')}`, `Depart ${localLabel(b, 'start')}`],
          calculation: `Gap ${formatDuration(gap)} vs your ${formatDuration(minConn)} buffer. Walking/transit time between places is not yet calculated (routing not connected).`,
          eventIds: [a.id, b.id], personIds: [person], action: { label: 'Review connection', kind: 'review' }, checkedAt, incomplete: true,
        });
      }
    }
  }

  // 4. Accommodation vs stops and nights covered
  const stays = live.filter((e) => e.category === 'accommodation' && e.start_date);
  for (const s of stays) {
    const place = `${s.venue ?? ''} ${s.address ?? ''} ${s.destination_name ?? ''}`.toLowerCase();
    const stop = stops.find((st) => st.city && place.includes(st.city.toLowerCase()));
    if (stop && stop.arrive_on && s.start_date !== stop.arrive_on) {
      out.push({
        fingerprint: fp('hotel_date_mismatch', [s], `${stop.id}:${stop.arrive_on}`), kind: 'hotel_date_mismatch', severity: 'high',
        title: `Hotel dates don't match your arrival in ${stop.city}`,
        impact: s.start_date! > stop.arrive_on ? `You arrive on ${stop.arrive_on} but check in on ${s.start_date}.` : `You check in on ${s.start_date} but arrive on ${stop.arrive_on}.`,
        evidence: [`${label(s)} check-in: ${s.start_date}`, `Stop ${stop.city}: arrive ${stop.arrive_on}`],
        eventIds: [s.id], personIds: who(s, opts.memberIds), action: { label: 'Review dates', kind: 'edit_event' }, checkedAt, incomplete: false,
      });
    }
  }
  if (trip.start_date && trip.end_date && stays.length > 0) {
    const covered = new Set<string>();
    for (const s of stays) {
      const end = s.end_date ?? addDays(s.start_date!, 1);
      for (let d = s.start_date!; d < end; d = addDays(d, 1)) covered.add(d);
    }
    const missing: string[] = [];
    for (let d = trip.start_date; d < trip.end_date; d = addDays(d, 1)) if (!covered.has(d)) missing.push(d);
    if (missing.length > 0 && missing.length <= 14) {
      out.push({
        fingerprint: `no_accommodation:${trip.id}:${hash(missing.join(','))}`, kind: 'no_accommodation', severity: 'low',
        title: missing.length === 1 ? `No accommodation on the night of ${missing[0]}` : `No accommodation on ${missing.length} nights`,
        impact: 'Add a stay, or dismiss this if you are travelling overnight or staying with friends.',
        evidence: missing.slice(0, 5).map((d) => `Night of ${d}`),
        eventIds: [], personIds: [], action: { label: 'Add a stay', kind: 'add_event' }, checkedAt, incomplete: false,
      });
    }
  }

  // 5. Events outside the trip dates
  if (trip.start_date && trip.end_date) {
    for (const e of live) {
      const day = e.start_date;
      if (day && (day < trip.start_date || day > trip.end_date)) {
        out.push({
          fingerprint: fp('outside_trip_dates', [e], `${trip.start_date}:${trip.end_date}`), kind: 'outside_trip_dates', severity: 'low',
          title: `${label(e)} is outside the trip dates`,
          impact: `The trip runs ${trip.start_date} to ${trip.end_date}.`,
          evidence: [`Event date: ${day}`],
          eventIds: [e.id], personIds: who(e, opts.memberIds), action: { label: 'Review', kind: 'edit_event' }, checkedAt, incomplete: false,
        });
      }
    }
  }

  const rank: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

export interface AttentionState {
  status: 'open' | 'dismissed' | 'snoozed' | 'resolved';
  reason?: string | null;
  until?: string | null;
}

/** Latest user action per fingerprint decides visibility; snoozes expire on their own. */
export function applyAttentionActions(
  findings: Finding[],
  actions: { fingerprint: string; action: 'dismissed' | 'snoozed' | 'resolved' | 'reopened'; reason: string | null; snoozed_until: string | null; created_at: string }[],
  now: Date,
): { visible: Finding[]; hidden: { finding: Finding; state: AttentionState }[] } {
  const latest = new Map<string, (typeof actions)[number]>();
  for (const a of [...actions].sort((x, y) => x.created_at.localeCompare(y.created_at))) latest.set(a.fingerprint, a);
  const visible: Finding[] = [];
  const hidden: { finding: Finding; state: AttentionState }[] = [];
  for (const f of findings) {
    const a = latest.get(f.fingerprint);
    if (!a || a.action === 'reopened') visible.push(f);
    else if (a.action === 'snoozed' && a.snoozed_until && new Date(a.snoozed_until) <= now) visible.push(f);
    else hidden.push({ finding: f, state: { status: a.action, reason: a.reason, until: a.snoozed_until } });
  }
  return { visible, hidden };
}
